import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Box,
  Typography,
  Paper,
  Alert,
  Divider,
} from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import Edit from '@mui/icons-material/Edit';
import Clear from '@mui/icons-material/Clear';
import Download from '@mui/icons-material/Download';
import SignatureCanvas from 'react-signature-canvas';
import { TermsAndConditions, SignatureData } from '../../types';
import { generateSignedAgreementPDF } from '../../utils/pdfGenerator';

interface SignatureDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (signatureData: SignatureData) => void;
  terms: TermsAndConditions;
  feeName: string;
  course?: string;
  signerName?: string;
}

export const SignatureDialog: React.FC<SignatureDialogProps> = ({
  open,
  onClose,
  onSubmit,
  terms,
  feeName,
  course,
  signerName = '',
}) => {
  const [accepted, setAccepted] = useState(false);
  const [signature, setSignature] = useState('');
  const [hasDrawnSignature, setHasDrawnSignature] = useState(false);
  const [errors, setErrors] = useState<{ accepted?: string; signature?: string; drawn?: string }>({});
  const [canvasWidth, setCanvasWidth] = useState(800);
  const sigPadRef = useRef<SignatureCanvas>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const updateCanvasSize = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        setCanvasWidth(width);
        if (sigPadRef.current) {
          sigPadRef.current.clear();
        }
        setHasDrawnSignature(false);
      }
    };

    if (open) {
      setTimeout(updateCanvasSize, 100);
      window.addEventListener('resize', updateCanvasSize);
    }

    return () => {
      window.removeEventListener('resize', updateCanvasSize);
    };
  }, [open]);

  // Tour-driven fill (step 13: Digital Signatures). The booth narrative
  // walks the buyer through accepting terms, typing a parent name, and
  // drawing the signature — all via cursor "clicks" that fire these
  // triggers. The trigger handlers do the actual state mutations since
  // the cursor click is visual-only. Cleanup happens naturally when
  // the dialog closes (parent unmounts the dialog tree).
  useEffect(() => {
    if (!open) return;
    let typingTimer: ReturnType<typeof setTimeout> | null = null;
    let drawTimer: ReturnType<typeof setTimeout> | null = null;

    // Animate a cursive "Lisa Johnson" signature on the canvas. Drawing
    // directly on the underlying canvas — signature_pad's internal
    // strokes state stays empty, but we control hasDrawnSignature so
    // form validation passes. toDataURL() picks up the painted pixels
    // for the order record either way.
    const drawFakeSignature = () => {
      const canvas = sigPadRef.current?.getCanvas?.();
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      // Coordinates designed for a 800px-wide × 150px-tall canvas;
      // the canvas auto-resizes to the container so use ratios of the
      // current width to keep the signature centred.
      const w = canvas.width;
      const h = canvas.height;
      const cx = w * 0.18;
      const baseY = h * 0.6;
      // Hand-tuned points that read as a cursive scribble. Treated as
      // a single stroke so we connect them with quadratic bezier
      // curves for a smoother line than straight segments.
      const pts: { x: number; y: number }[] = [
        { x: cx,            y: baseY + 5  },
        { x: cx + 12,       y: baseY - 18 },
        { x: cx + 22,       y: baseY      },
        { x: cx + 30,       y: baseY - 26 },
        { x: cx + 42,       y: baseY + 4  },
        { x: cx + 56,       y: baseY - 12 },
        { x: cx + 70,       y: baseY      },
        { x: cx + 82,       y: baseY - 22 },
        { x: cx + 96,       y: baseY + 2  },
        { x: cx + 112,      y: baseY - 14 },
        { x: cx + 128,      y: baseY + 6  },
        { x: cx + 148,      y: baseY - 30 },
        { x: cx + 168,      y: baseY      },
        { x: cx + 186,      y: baseY - 18 },
        { x: cx + 204,      y: baseY + 4  },
        { x: cx + 222,      y: baseY - 22 },
        { x: cx + 244,      y: baseY      },
        { x: cx + 266,      y: baseY - 16 },
        { x: cx + 288,      y: baseY + 6  },
        { x: cx + 310,      y: baseY - 24 },
        { x: cx + 332,      y: baseY      },
        { x: cx + 354,      y: baseY - 12 },
        { x: cx + 380,      y: baseY      },
      ];
      ctx.strokeStyle = 'black';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      let i = 0;
      const drawNext = () => {
        if (i >= pts.length - 1) {
          setHasDrawnSignature(true);
          return;
        }
        const a = pts[i];
        const b = pts[i + 1];
        ctx.beginPath();
        ctx.moveTo(a.x, a.y);
        ctx.lineTo(b.x, b.y);
        ctx.stroke();
        i += 1;
        drawTimer = setTimeout(drawNext, 60);
      };
      drawTimer = setTimeout(drawNext, 60);
    };

    // Scroll the dialog's content area so a given target lands near
    // the top of the visible area. The booth narrative needs each
    // action visible as it happens — without this, the spotlight
    // target (the whole dialog) re-anchors to the dialog's top, which
    // pins the long terms section in view and pushes the name field /
    // signature canvas off-screen. Quick attribute-based selector so
    // we don't have to thread refs everywhere.
    const scrollTargetIntoView = (selector: string) => {
      const dialogContent = document.querySelector('.MuiDialogContent-root') as HTMLElement | null;
      const target = document.querySelector(selector) as HTMLElement | null;
      if (!dialogContent || !target) return;
      const cRect = dialogContent.getBoundingClientRect();
      const tRect = target.getBoundingClientRect();
      const offset = tRect.top - cRect.top - 24;
      dialogContent.scrollTo({ top: dialogContent.scrollTop + offset, behavior: 'smooth' });
    };

    const onTrigger = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const trigger = detail?.trigger;
      if (trigger === 'sig-accept-terms') {
        setAccepted(true);
        scrollTargetIntoView('[data-tour="sig-accept-checkbox"]');
      } else if (trigger === 'sig-fill-name') {
        scrollTargetIntoView('[data-tour="sig-name-field"]');
        const target = 'Lisa Johnson';
        let i = 0;
        const typeNext = () => {
          i += 1;
          setSignature(target.slice(0, i));
          if (i < target.length) {
            typingTimer = setTimeout(typeNext, 90);
          }
        };
        typingTimer = setTimeout(typeNext, 200);
      } else if (trigger === 'sig-draw-signature') {
        scrollTargetIntoView('[data-tour="sig-canvas"]');
        // Brief delay so the scroll animation settles before the
        // drawing starts — otherwise the canvas is mid-tween and the
        // ink ends up on the wrong pixels.
        drawTimer = setTimeout(() => {
          drawFakeSignature();
        }, 350);
      }
    };

    window.addEventListener('cc-tour-trigger', onTrigger);
    return () => {
      window.removeEventListener('cc-tour-trigger', onTrigger);
      if (typingTimer) clearTimeout(typingTimer);
      if (drawTimer) clearTimeout(drawTimer);
    };
  }, [open]);

  const validateForm = (): boolean => {
    const newErrors: { accepted?: string; signature?: string; drawn?: string } = {};

    if (!accepted) {
      newErrors.accepted = 'You must accept the terms and conditions to proceed';
    }

    if (terms.requiresSignature) {
      if (!signature.trim()) {
        newErrors.signature = 'Name is required';
      }
      if (!hasDrawnSignature) {
        newErrors.drawn = 'Please draw your signature above';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const drawnSignature = sigPadRef.current?.toDataURL('image/png');

      const signatureData: SignatureData = {
        accepted: true,
        signedBy: signature.trim(),
        signedAt: new Date(),
        drawnSignature,
      };

      onSubmit(signatureData);
      handleClose();
    }
  };

  const handleClose = () => {
    setAccepted(false);
    setSignature('');
    setHasDrawnSignature(false);
    setErrors({});
    if (sigPadRef.current) {
      sigPadRef.current.clear();
    }
    onClose();
  };

  const handleClearSignature = () => {
    if (sigPadRef.current) {
      sigPadRef.current.clear();
    }
    setHasDrawnSignature(false);
    if (errors.drawn) {
      setErrors(prev => ({ ...prev, drawn: undefined }));
    }
  };

  const handleDownloadPreview = async () => {
    if (!validateForm()) {
      return;
    }

    const drawnSignature = sigPadRef.current?.toDataURL('image/png');

    const signatureData: SignatureData = {
      accepted: true,
      signedBy: signature.trim(),
      signedAt: new Date(),
      drawnSignature,
    };

    await generateSignedAgreementPDF(feeName, terms, signatureData, course);
  };

  const renderContent = (content: string) => {
    return content.split('\n').map((line, index) => {
      if (line.startsWith('**') && line.endsWith('**')) {
        const text = line.slice(2, -2);
        if (text.includes(':')) {
          return (
            <Typography key={index} variant="subtitle2" fontWeight={700} sx={{ mt: 2, mb: 0.5 }}>
              {text}
            </Typography>
          );
        }
        return (
          <Typography key={index} variant="h6" fontWeight={700} sx={{ mt: 3, mb: 1 }}>
            {text}
          </Typography>
        );
      }
      if (line.startsWith('- ')) {
        return (
          <Typography key={index} variant="body2" sx={{ ml: 2, mb: 0.5 }}>
            • {line.slice(2)}
          </Typography>
        );
      }
      if (line.match(/^\d+\./)) {
        return (
          <Typography key={index} variant="body2" sx={{ mt: 1, mb: 0.5 }}>
            {line}
          </Typography>
        );
      }
      if (line.trim() === '') {
        return <Box key={index} sx={{ height: 8 }} />;
      }
      return (
        <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
          {line}
        </Typography>
      );
    });
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, maxHeight: '90vh' },
        'data-tour': 'sig-dialog',
      } as React.ComponentProps<typeof Dialog>['PaperProps']}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Edit color="primary" />
          <Box>
            <Typography variant="h6" fontWeight={600}>
              {terms.title}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {feeName}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Paper
          sx={{
            p: 3,
            mb: 3,
            maxHeight: '400px',
            overflowY: 'auto',
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'grey.200',
          }}
        >
          {renderContent(terms.content)}
        </Paper>

        <Alert severity="info" sx={{ mb: 3 }}>
          Please read the terms carefully before accepting and signing below.
        </Alert>

        <Box sx={{ mb: 3 }}>
          <FormControlLabel
            data-tour="sig-accept-checkbox"
            control={
              <Checkbox
                checked={accepted}
                onChange={(e) => {
                  setAccepted(e.target.checked);
                  if (e.target.checked && errors.accepted) {
                    setErrors(prev => ({ ...prev, accepted: undefined }));
                  }
                }}
                color="primary"
              />
            }
            label={
              <Typography variant="body2">
                I have read and agree to the terms and conditions stated above
              </Typography>
            }
          />
          {errors.accepted && (
            <Typography variant="caption" color="error" sx={{ display: 'block', ml: 4, mt: 0.5 }}>
              {errors.accepted}
            </Typography>
          )}
        </Box>

        {terms.requiresSignature && (
          <>
            <Divider sx={{ mb: 3 }} />
            <Box>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                1. Type Your Full Name
              </Typography>
              <TextField
                data-tour="sig-name-field"
                fullWidth
                label={terms.signatureLabel || 'Full Name'}
                value={signature}
                onChange={(e) => {
                  setSignature(e.target.value);
                  if (e.target.value.trim() && errors.signature) {
                    setErrors(prev => ({ ...prev, signature: undefined }));
                  }
                }}
                placeholder="Type your full name"
                error={!!errors.signature}
                helperText={errors.signature}
                required
                InputProps={{
                  startAdornment: (
                    <Edit sx={{ mr: 1, color: 'text.secondary' }} />
                  ),
                }}
                sx={{
                  mb: 3,
                  '& .MuiInputBase-input': {
                    fontFamily: 'cursive',
                    fontSize: '1.1rem',
                  }
                }}
              />

              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                2. Draw Your Signature
              </Typography>
              <Paper
                ref={containerRef}
                data-tour="sig-canvas"
                sx={{
                  border: errors.drawn ? '2px solid' : '2px solid',
                  borderColor: errors.drawn ? 'error.main' : 'grey.300',
                  borderRadius: 1,
                  overflow: 'hidden',
                  bgcolor: 'white',
                }}
              >
                <SignatureCanvas
                  ref={sigPadRef}
                  canvasProps={{
                    width: canvasWidth,
                    height: 150,
                    style: { width: '100%', height: '150px', display: 'block' }
                  }}
                  backgroundColor="white"
                  penColor="black"
                  onEnd={() => {
                    setHasDrawnSignature(true);
                    if (errors.drawn) {
                      setErrors(prev => ({ ...prev, drawn: undefined }));
                    }
                  }}
                />
              </Paper>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <Typography variant="caption" color={errors.drawn ? 'error' : 'text.secondary'}>
                  {errors.drawn || 'Sign above using your mouse or touchscreen'}
                </Typography>
                <Button
                  size="small"
                  startIcon={<Clear />}
                  onClick={handleClearSignature}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          </>
        )}

        <Alert severity="warning" sx={{ mt: 3 }}>
          <Typography variant="caption">
            <strong>Legal Notice:</strong> By accepting and signing this document, you are entering into a legally binding agreement. The timestamp and your signature will be recorded.
          </Typography>
        </Alert>
      </DialogContent>
      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          // Mobile: stack vertically (column-reverse) so the order
          // top-to-bottom is Accept → Download → Cancel even though
          // the source order keeps Cancel first for keyboard / a11y.
          // Desktop: row, with Cancel pushed left via auto margin.
          flexDirection: { xs: 'column-reverse', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 1,
          // MUI's DialogActions adds `margin-left: 8px` to every
          // button after the first; flatten that on mobile so the
          // stacked buttons are flush left.
          '& > :not(:first-of-type)': { ml: { xs: 0, sm: 1 } },
        }}
      >
        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{ mr: { xs: 0, sm: 'auto' } }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleDownloadPreview}
          variant="outlined"
          startIcon={<Download />}
          disabled={!accepted || (terms.requiresSignature && (!signature.trim() || !hasDrawnSignature))}
        >
          Download Preview
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          startIcon={<CheckCircle />}
          disabled={!accepted || (terms.requiresSignature && (!signature.trim() || !hasDrawnSignature))}
        >
          Accept and Sign
        </Button>
      </DialogActions>
    </Dialog>
  );
};
